// Shared row/filter types and display formatters live in the design system
// (packages/ui) so the launcher and the site share one definition. They are
// imported and re-exported here so every existing `from './api'` keeps working.
import type { Fakes, ServerRow, ServersResp, FilterOptions, ServerFilters, ModInfo, CountryInfo, FeaturedPost, TrendingItem, Banner } from '@dzm/ui/types'
export type { Fakes, ServerRow, ServersResp, FilterOptions, ServerFilters, ModInfo, CountryInfo }
import type {
  SimilarServer, SimilarResp, SimilarQuery, HeatCell, Mod, ServerDetail, Severity, ServerNotice, ReportReason, RestartInfo,
  STPoint, RankRange, RankPoint, VoteAxisKey, VoteAxisAgg, VotesAgg,
} from '@dzm/ui/types'
export type {
  SimilarServer, SimilarResp, SimilarQuery, HeatCell, Mod, ServerDetail, Severity, ServerNotice, ReportReason, RestartInfo,
  STPoint, RankRange, RankPoint, VoteAxisKey, VoteAxisAgg, VotesAgg,
}
export { fmt, flag, workshop, mapLabel, humanSize } from '@dzm/ui/format'
import { serversQuery, filtersQuery, type FiltersCtx, discoverQuery, similarQuery } from '@dzm/ui/api-query'
// Range lives with the chart recipes in the design system so the launcher and
// the site share one definition. Imported AND re-exported: a bare re-export
// would leave this module resolving `Range` to the DOM global of the same name.
import type { Range } from '@dzm/ui/lib'
export type { Range }

export interface Service { active: string; memory: string; restarts: string; since: string }
// FeaturedPost, TrendingItem and Banner moved to packages/ui/src/types.ts
// (shared with the launcher); re-exported here so callers do not change.
export type { FeaturedPost, TrendingItem, Banner } from '@dzm/ui/types'

export interface Overview {
  service: Service | null
  online: number; online_raw: number; suspect_online: number
  players: number; players_raw: number
  servers_tracked: number; sweep_total: number
  snapshots_est: number | null; db_size: string; last_sweep: string | null
  dayz_global?: number | null; dayz_global_24h?: number | null  // Steam-wide DayZ players (now / ~24h ago)
}
export interface TPoint { t: string; online: number; players: number }
export interface MapRow { map: string; servers: number; players: number }
export interface ModRow { id: string | null; name: string | null; count: number }

export type TrendRange = '7d' | '14d' | '30d'
export interface TrendPoint { t: string; v: number }
export interface TrendPage<T> { items: T[]; total: number }
export interface MapTrend {
  map: string; servers: number; players: number
  history: TrendPoint[]; delta_pct: number
}
export interface ModTrend {
  id: string | null; name: string | null; count: number
  history: TrendPoint[]; delta_pct: number
}


// The read API key is injected by nginx at the edge (X-API-Key header) for the
// player vhost, so it never ships in this bundle. Same-origin fetch is enough here.
// index.html fires the default homepage requests before this bundle even
// finishes downloading and parks the promises on window.__early (keyed by
// path + sorted query). First matching call here consumes the entry — then
// deletes it, so refreshes and every non-default query hit the network
// exactly as before.
type EarlyWindow = { __early?: Record<string, Promise<unknown>>; __earlyKey?: (u: string) => string }
function takeEarly(url: string): Promise<unknown> | null {
  const w = window as EarlyWindow
  if (!w.__early || !w.__earlyKey) return null
  const k = w.__earlyKey(url)
  if (!(k in w.__early)) return null
  const hit = w.__early[k]
  delete w.__early[k]
  return hit
}

// `signal` lets a caller abort a request it no longer needs (a superseded
// filter change, a client-side timeout); the early homepage fetch cannot be
// aborted, but it is the one request every visitor wants anyway.
async function j<T>(url: string, headers?: Record<string, string>, signal?: AbortSignal): Promise<T> {
  if (!headers) {
    const early = takeEarly(url)
    if (early) {
      const data = await early
      if (data !== null) return data as T
      // early fetch failed — fall through to a normal request
    }
  }
  const init: RequestInit = {}
  if (headers) init.headers = headers
  if (signal) init.signal = signal
  const r = await fetch(url, init)
  if (!r.ok) throw new Error('http ' + r.status)
  return r.json() as Promise<T>
}
async function post<T>(url: string, body: unknown, headers?: Record<string, string>): Promise<T> {
  const r = await fetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, body: JSON.stringify(body),
  })
  if (!r.ok) {
    // Surface the server's own message (FastAPI `detail`) instead of a bare
    // status: a 409 "that name is taken" was reaching users as "http 409".
    const data = await r.json().catch(() => ({})) as Record<string, unknown>
    const msg = typeof data?.detail === 'string' ? data.detail : 'http ' + r.status
    const e = new Error(msg) as Error & { status?: number }; e.status = r.status; throw e
  }
  return r.json() as Promise<T>
}

/* ------------------------------------------------------- playstyle votes
 * Five bipolar style axes (-2..+2, null = skipped). The anonymous voter token
 * lives in an HttpOnly cookie set by the API; we mirror it in localStorage and
 * send it as X-Voter so a lost cookie doesn't orphan the vote. */
const VOTER_KEY = 'dzm_voter'
function voterHeaders(): Record<string, string> {
  try { const t = localStorage.getItem(VOTER_KEY); return t ? { 'X-Voter': t } : {} } catch { return {} }
}

// Payload's banner global with depth=1: `image` is the populated media doc
// (or null). Text fields can come back null when left blank in the admin.
interface PayloadSlide {
  enabled?: boolean | null
  headline: string | null
  subhead: string | null
  link_url: string | null
  link_label: string | null
  align: 'left' | 'center' | null
  image: { url?: string | null } | null
}
interface PayloadBanner {
  enabled: boolean
  headline: string | null
  subhead: string | null
  link_url: string | null
  link_label: string | null
  align: 'left' | 'center'
  image: { url?: string | null } | null
  slides?: PayloadSlide[] | null   // carousel: when present + non-empty, wins over the single fields
}
// Reduce any URL (absolute or relative) to a same-origin path + query, so a
// Payload-emitted absolute URL (built from its beta server URL) still loads
// from whatever host is serving the page.
function sameOriginPath(u: string): string {
  try {
    const x = new URL(u, location.origin)
    return x.pathname + x.search
  } catch {
    return u
  }
}

export const api = {
  overview: () => j<Overview>('/api/overview'),
  // Post pinned to the homepage panel. Falls back server-side to the newest
  // published post, so this never returns null just because nothing is pinned.
  blogFeatured: () => j<{ post: FeaturedPost | null }>('/api/blog/featured'),
  timeseries: (range: Range, fakes: Fakes) => j<TPoint[]>(`/api/timeseries?range=${range}&fakes=${fakes}`),
  // Full-catalog mod search for the filter panel (the /api/filters facet only
  // carries the most popular mods).
  modsSearch: (q: string) => j<{ id: string | null; name: string | null; count: number }[]>(
    `/api/mods/search?q=${encodeURIComponent(q)}`),
  // The query builder is shared with the launcher (packages/ui/src/api-query.ts).
  servers: (f: ServerFilters) => j<ServersResp>('/api/servers?' + serversQuery(f)),
  // Query builder shared with the launcher (packages/ui/src/api-query.ts).
  trending: (country?: string[], limit = 12) => j<TrendingItem[]>(`/api/trending?${discoverQuery(country, limit)}`),
  upcoming: (country?: string[], limit = 12) => j<TrendingItem[]>(`/api/upcoming?${discoverQuery(country, limit)}`),
  server: (id: number) => j<ServerDetail>(`/api/server/${id}`),
  similar: (id: number, q: SimilarQuery = {}) => j<SimilarResp>(`/api/server/${id}/similar?${similarQuery(q)}`),
  heatmap: (id: number) => j<HeatCell[]>(`/api/server/${id}/heatmap`),
  serverSeries: (id: number, range: Range) => j<STPoint[]>(`/api/server/${id}/timeseries?range=${range}`),
  // Daily rank position over time (coarse ranges, no 1d — rank is a daily snapshot).
  rankHistory: (id: number, range: RankRange) => j<RankPoint[]>(`/api/server/${id}/rank-history?range=${range}`),
  maps: (fakes: Fakes) => j<MapRow[]>(`/api/maps?fakes=${fakes}`),
  mods: (fakes: Fakes) => j<ModRow[]>(`/api/mods?fakes=${fakes}`),
  // Trends: per-item daily history + delta % over a range. Used by the
  // Maps & mods home section to render sparklines and "up / down" deltas.
  // q = substring search, offset = pagination start (limit defines page size).
  mapsSeries: (range: TrendRange = '7d', limit = 10, offset = 0, q = '', signal?: AbortSignal) =>
    j<TrendPage<MapTrend>>(`/api/maps/series?range=${range}&limit=${limit}&offset=${offset}&q=${encodeURIComponent(q)}`, undefined, signal),
  modsSeries: (range: TrendRange = '7d', limit = 10, offset = 0, q = '', signal?: AbortSignal) =>
    j<TrendPage<ModTrend>>(`/api/mods/series?range=${range}&limit=${limit}&offset=${offset}&q=${encodeURIComponent(q)}`, undefined, signal),
  // Facet counts are computed over the CURRENT context (scope/mode/online/
  // population) so the numbers reflect what you're viewing. Called with no ctx
  // for the global region map; called with ctx from the browse panel.
  filters: (ctx?: FiltersCtx) => {
    const qs = filtersQuery(ctx)
    return j<FilterOptions>('/api/filters' + (qs ? '?' + qs : ''))
  },
  reportReasons: () => j<ReportReason[]>('/api/report-reasons'),
  report: (id: number, reason: string, message: string) =>
    post<{ ok: boolean; duplicate: boolean }>(`/api/server/${id}/report`, { reason, message }),
  // Community playstyle profile + the viewer's own rating (when their voter
  // cookie/token matches). Casting a vote stores the minted token mirror.
  votes: (id: number) => j<VotesAgg>(`/api/server/${id}/votes`, voterHeaders()),
  vote: async (id: number, axes: Record<VoteAxisKey, number | null>) => {
    const r = await post<{ ok: boolean; updated: boolean; voter: string | null; unlocked?: string[] }>(
      `/api/server/${id}/vote`, { played: true, axes }, voterHeaders())
    if (r.voter) { try { localStorage.setItem(VOTER_KEY, r.voter) } catch { /* private mode */ } }
    return r
  },
  contact: (email: string, subject: string, body: string, hp: string = '') =>
    post<{ ok: boolean; id: number | null }>('/api/contact', { email, subject, body, hp }),
  // Admin-editable hero banner (image + headline + subhead + optional CTA link).
  // Caller falls back to the built-in hero copy when there's no image.
  //
  // CMS migration: prefer the Payload-managed banner global. Until a banner
  // image is uploaded into Payload, fall back to the legacy FastAPI banner so
  // the hero never disappears mid-migration. Once the image lives in Payload,
  // this transparently switches over with no further code or deploy.
  banner: async (): Promise<Banner> => {
    try {
      const p = await j<PayloadBanner>('/cms-api/globals/banner?depth=1')
      if (p && p.enabled && p.image && p.image.url) {
        return {
          enabled: p.enabled,
          headline: p.headline ?? '',
          subhead: p.subhead ?? '',
          link_url: p.link_url ?? '',
          link_label: p.link_label ?? '',
          align: p.align,
          // Normalise to the current origin: Payload may emit an absolute URL
          // built from its server URL (which still points at beta). Using the
          // path only keeps the image same-origin on whatever host serves it.
          image_url: sameOriginPath(p.image.url),
        }
      }
    } catch {
      // Payload unreachable or malformed: fall through to the legacy banner.
    }
    return j<Banner>('/api/banner')
  },
  // Carousel feed. Prefers the Payload global's `slides[]` array (each an
  // enabled slide with an image), falls back to the legacy single Payload
  // banner, then the legacy FastAPI banner — so it never breaks mid-migration.
  // Returns [] when nothing is published, and the caller shows the text hero.
  banners: async (): Promise<Banner[]> => {
    // DEV-ONLY demo stub (remove): /?bannerdemo=1 renders 3 fake slides locally.
    if (typeof location !== 'undefined' && location.search.includes('bannerdemo')) {
      const img = (c: string) => `data:image/svg+xml,` + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='300'><rect width='1200' height='300' fill='${c}'/><circle cx='980' cy='150' r='170' fill='rgba(255,60,21,.20)'/></svg>`)
      return [
        { enabled: true, headline: 'VOTE YOUR SERVER', subhead: 'Help other survivors find their home · dayzmetrics.com', link_url: '', link_label: '', align: 'left', image_url: img('#3a2418') },
        { enabled: true, headline: 'SURVIVORS, I NEED YOUR HELP', subhead: 'Rate the servers you play · {servers} live now', link_url: '', link_label: '', align: 'center', image_url: img('#1c2a33') },
        { enabled: true, headline: 'KNOW BEFORE YOU SPAWN', subhead: 'Population, mods, playstyle — fakes filtered', link_url: '', link_label: '', align: 'left', image_url: img('#2a1c2a') },
      ]
    }
    const fromSlide = (sl: PayloadSlide): Banner | null =>
      sl && sl.enabled !== false && sl.image && sl.image.url
        ? { enabled: true, headline: sl.headline ?? '', subhead: sl.subhead ?? '',
            link_url: sl.link_url ?? '', link_label: sl.link_label ?? '',
            align: sl.align ?? 'left', image_url: sameOriginPath(sl.image.url) }
        : null
    try {
      // The head-start prefetch covers this URL, so j() consumes it via
      // window.__early like every other homepage request.
      const p = await j<PayloadBanner>('/cms-api/globals/banner?depth=1')
      if (p && Array.isArray(p.slides) && p.slides.length) {
        const out = p.slides.map(fromSlide).filter((x): x is Banner => x !== null)
        if (out.length) return out
      }
      if (p && p.enabled && p.image && p.image.url) {
        return [{ enabled: true, headline: p.headline ?? '', subhead: p.subhead ?? '',
                  link_url: p.link_url ?? '', link_label: p.link_label ?? '',
                  align: p.align, image_url: sameOriginPath(p.image.url) }]
      }
    } catch { /* Payload unreachable: fall through */ }
    try {
      const b = await j<Banner>('/api/banner')
      if (b && b.enabled && b.image_url) return [b]
    } catch { /* */ }
    return []
  },
  // Lightweight resolvers used when the SPA boots on a deep-link URL like
  // /mod/{id} or /servers/{country-slug}. They return the human label + the
  // current live server count so the page can render a single context banner
  // ("Showing 12 servers running Community Framework") without a second hop.
  modInfo: (id: number) => j<ModInfo>(`/api/mod-info/${id}`),
  countryInfo: (slug: string) => j<CountryInfo>(`/api/country-info/${encodeURIComponent(slug)}`),
  // Public, read-only profile by handle (404 when private/missing).
  publicProfile: (handle: string) => j<PublicProfileData>(`/api/u/${encodeURIComponent(handle)}`),
}

export interface PublicProfileData {
  handle: string; display_name: string | null; avatar_url: string | null
  member_since: string | null; achievements: UserAchievement[]; earned: number
  invited: number; favourite_ids: number[]; dayz_hours: number | null
}


/* --------------------------------------------------------------- auth / accounts
 * Session lives in an HttpOnly `uid` cookie set by the backend, so the SPA can't
 * read it — it asks /api/auth/me instead. All auth calls must send the cookie, so
 * these helpers use credentials:'include' (same-origin in prod via nginx). */
export type Provider = 'discord' | 'google' | 'steam'
export interface AuthUser {
  id: number; display_name: string | null; avatar_url: string | null
  email: string | null; is_admin: boolean; tier: 'free' | 'premium'
  premium_until: string | null; handle: string | null
  email_verified: boolean; has_password: boolean; profile_public: boolean
  member_since: string | null; wants_updates: boolean
  referral_code: string | null; referral_url: string | null
}
export interface AuthIdentity {
  provider: Provider; uid: string; username: string | null
  avatar_url: string | null; profile: Record<string, unknown>
}
export interface AuthMe { user: AuthUser | null; identities?: AuthIdentity[] }

async function jc<T>(url: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(url, { credentials: 'include', signal })
  if (!r.ok) {
    // Surface the server's own message (FastAPI `detail`) instead of a bare
    // status: a 409 "that name is taken" was reaching users as "http 409".
    const data = await r.json().catch(() => ({})) as Record<string, unknown>
    const msg = typeof data?.detail === 'string' ? data.detail : 'http ' + r.status
    const e = new Error(msg) as Error & { status?: number }; e.status = r.status; throw e
  }
  return r.json() as Promise<T>
}
async function postc<T>(url: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const r = await fetch(url, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal,
  })
  const data = await r.json().catch(() => ({})) as Record<string, unknown>
  if (!r.ok) {
    const msg = typeof data?.detail === 'string' ? data.detail : 'Something went wrong.'
    const e = new Error(msg) as Error & { status?: number }; e.status = r.status; throw e
  }
  return data as T
}

async function delc<T>(url: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(url, { method: 'DELETE', credentials: 'include', signal })
  if (!r.ok) throw new Error('http ' + r.status)
  return r.json().catch(() => ({})) as Promise<T>
}
async function patchc<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  })
  const data = await r.json().catch(() => ({})) as Record<string, unknown>
  if (!r.ok) {
    const msg = typeof data?.detail === 'string' ? data.detail : 'Something went wrong.'
    const e = new Error(msg) as Error & { status?: number }; e.status = r.status; throw e
  }
  return data as T
}

export interface UserAchievement {
  key: string; title: string; description: string; asset: string
  threshold: number; value: number; unlocked: boolean; unlocked_at: string | null
}
export interface UserReferrals { code: string | null; url: string | null; confirmed: number; pending: number }
// A server this account rated, with the user's own axis values (-2..+2, null = skipped).
export interface UserVote {
  server_id: number
  axes: Record<VoteAxisKey, number | null>
  updated_at: string | null
}

// Favourites calls give up after a while: a toggle the API never answers must
// not hold the pull-on-focus (it waits for toggles in flight) until the browser
// gives up on the request.
const FAV_TIMEOUT_MS = 10_000
const favTimeout = () => AbortSignal.timeout(FAV_TIMEOUT_MS)
export const userApi = {
  favourites: () => jc<{ ids: number[] }>('/api/user/favourites', favTimeout()),
  favAdd: (sid: number) => postc<{ ok: boolean }>(`/api/user/favourites/${sid}`, {}, favTimeout()),
  favRemove: (sid: number) => delc<{ ok: boolean }>(`/api/user/favourites/${sid}`, favTimeout()),
  favMerge: (ids: number[]) => postc<{ ids: number[] }>('/api/user/favourites/merge', { ids }, favTimeout()),
  // Merge an anon vote into the account on login. The dzm_voter cookie rides along
  // with credentials:'include'; we also pass the localStorage mirror as X-Voter.
  claimVotes: async () => {
    const r = await fetch('/api/user/claim-votes', {
      method: 'POST', credentials: 'include', headers: { ...voterHeaders() },
    })
    return (r.ok ? r.json() : { merged: 0, unlocked: [] }) as Promise<{ merged: number; unlocked: string[] }>
  },
  achievements: () => jc<{ achievements: UserAchievement[] }>('/api/user/achievements'),
  votes: () => jc<{ votes: UserVote[] }>('/api/user/votes'),
  settings: (patch: { handle?: string | null; profile_public?: boolean; display_name?: string | null; wants_updates?: boolean }) =>
    patchc<{ ok: boolean }>('/api/user/settings', patch),
  setPassword: (password: string, current?: string) =>
    postc<{ ok: boolean }>('/api/user/password', { password, current }),
  referrals: () => jc<UserReferrals>('/api/user/referrals'),
  // image is a data URL (data:image/...;base64,...) read from a file input.
  uploadAvatar: (image: string) => postc<{ ok: boolean; avatar_url: string }>('/api/user/avatar', { image }),
  deleteAccount: () => postc<{ ok: boolean }>('/api/user/delete', {}),
}

export const auth = {
  me: () => jc<AuthMe>('/api/auth/me'),
  providers: () => jc<Record<Provider, boolean>>('/api/auth/providers'),
  // OAuth + Steam are full-navigation redirects (the backend bounces to the provider).
  // Carry any pending invite code so an OAuth *signup* is attributed too (not when
  // linking an extra provider to an already-logged-in account).
  loginUrl: (p: Provider, redirect = '/', link = false) => {
    let ref = ''
    if (!link) { try { ref = localStorage.getItem('dzm_ref') || '' } catch { /* private mode */ } }
    return `/api/auth/${p}/login?redirect=${encodeURIComponent(redirect)}${link ? '&link=1' : ''}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`
  },
  register: (email: string, password: string, ref?: string | null, updates?: boolean) =>
    postc<{ ok: boolean; needs_verify: boolean }>('/api/auth/register', { email, password, ref, updates }),
  login: (email: string, password: string) =>
    postc<{ ok: boolean }>('/api/auth/login', { email, password }),
  logout: () => postc<{ ok: boolean }>('/api/auth/logout', {}),
  requestReset: (email: string) => postc<{ ok: boolean }>('/api/auth/request-reset', { email }),
  reset: (token: string, password: string) => postc<{ ok: boolean }>('/api/auth/reset', { token, password }),
  resendVerify: () => postc<{ ok: boolean }>('/api/auth/resend-verify', {}),
  unlink: (p: Provider) => postc<{ ok: boolean }>(`/api/auth/unlink/${p}`, {}),
}

